import type { GetMessagesQuery } from '../generated/hooks';

/**
 * Target {@link HTMLDivElement.scrollTop} to keep the latest thread visible. Prefers
 * “full bottom” (input + specials), but if Specials/controls are tall, caps scroll so
 * the last message/typing row (anchor) stays in view.
 */
function getIdealChatScrollTop(
  scrollContainer: HTMLElement,
  _messageListEnd: HTMLElement | null,
  lastBlockBeforeControls: HTMLElement | null,
): number {
  const maxScroll = Math.max(0, scrollContainer.scrollHeight - scrollContainer.clientHeight);
  if (maxScroll === 0) {
    return 0;
  }
  if (!lastBlockBeforeControls) {
    return maxScroll;
  }
  const c = scrollContainer.getBoundingClientRect();
  const b = lastBlockBeforeControls.getBoundingClientRect();
  const lastBlockTop = b.top - c.top + scrollContainer.scrollTop;
  const MARGIN = 8;
  return Math.min(maxScroll, Math.max(0, lastBlockTop - MARGIN));
}

export function lastMessageId(m: GetMessagesQuery['messages'] | null | undefined) {
  return m && m.length > 0 ? m[m.length - 1].id : undefined;
}

/** New / resized list, or first time we have GQL `messages` with a context `lastMessage`. */
export function messagesListTouched(
  prevMessages: GetMessagesQuery['messages'] | null | undefined,
  messages: GetMessagesQuery['messages'] | null | undefined,
  hadPrevContextLast: boolean,
): boolean {
  return (
    (prevMessages == null && messages != null) ||
    (prevMessages != null && messages != null && prevMessages.length !== messages.length) ||
    Boolean(!hadPrevContextLast && messages?.length)
  );
}

export const AUTOSCROLL_DEBOUNCE_MS = 100;
export const PROGRAMMATIC_SCROLL_IGNORE_MS = 900;
const NEAR_BOTTOM_PX = 32;
const SCROLL_STEP_PX = 1;

export function getChatIdealTop(
  scrollEl: HTMLElement,
  listEnd: HTMLDivElement | null,
  lastBlock: HTMLDivElement | null,
): number {
  return getIdealChatScrollTop(scrollEl, listEnd, lastBlock);
}

function isNearBottom(scrollTop: number, ideal: number, factor = 1) {
  return Math.abs(scrollTop - ideal) <= NEAR_BOTTOM_PX * factor;
}

/** `deltaY` from wheel: pause on up, maybe resume on down. */
export function wheelAutoscrollEffect(deltaY: number, scrollTop: number, ideal: number) {
  if (deltaY < 0) return 'pause' as const;
  if (deltaY > 0 && isNearBottom(scrollTop, ideal, 1.5)) return 'resume' as const;
  return 'pause' as const;
}

/**
 * When user is reading history, follow mode pauses; scrolling back near the end resumes it.
 */
export function nextUserPaused(
  wasPaused: boolean,
  hasSyncedProgrammatic: boolean,
  scrollTop: number,
  ideal: number,
  previousScrollTop: number | undefined,
): boolean {
  if (wasPaused) {
    const movedDown = previousScrollTop != null && scrollTop > previousScrollTop + SCROLL_STEP_PX;
    if (movedDown && isNearBottom(scrollTop, ideal, 1)) {
      return false;
    }
    return true;
  }
  if (hasSyncedProgrammatic && scrollTop < ideal - NEAR_BOTTOM_PX) {
    return true;
  }
  if (previousScrollTop != null && scrollTop < previousScrollTop - SCROLL_STEP_PX) {
    return true;
  }
  return false;
}
